import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Image as ImageIcon, ArrowRight, Heart } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getLikedSet = () => {
  try { return new Set(JSON.parse(localStorage.getItem('kiziloren_liked_albums') || '[]')); }
  catch { return new Set(); }
};
const setLikedSet = (set) => {
  localStorage.setItem('kiziloren_liked_albums', JSON.stringify([...set]));
};

export default function Galeri() {
  const [albums, setAlbums] = useState([]);
  const [orphanCount, setOrphanCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [likedAlbums, setLikedAlbums] = useState(() => getLikedSet());
  const [pendingLikes, setPendingLikes] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      try {
        const [albumsRes, orphanRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/albums`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/gallery?album_id=none`).then(r => r.json()),
        ]);
        const sorted = Array.isArray(albumsRes)
          ? [...albumsRes].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
          : [];
        setAlbums(sorted);
        setOrphanCount(Array.isArray(orphanRes) ? orphanRes.length : 0);
      } catch {
        toast.error('Galeri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLike = async (e, album) => {
    e.preventDefault();
    e.stopPropagation();
    if (album.id === 'genel' || pendingLikes.has(album.id) || likedAlbums.has(album.id)) return;
    setPendingLikes(prev => new Set([...prev, album.id]));
    try {
      const r = await fetch(`${BACKEND_URL}/api/albums/${album.id}/like`, { method: 'POST' });
      if (!r.ok) throw new Error('like failed');
      const data = await r.json();
      setAlbums(prev => prev.map(a => a.id === album.id ? { ...a, like_count: data.like_count } : a));
      setLikedAlbums(prev => {
        const next = new Set(prev);
        next.add(album.id);
        setLikedSet(next);
        return next;
      });
      toast.success('Beğeniniz kaydedildi');
    } catch {
      toast.error('Beğeni gönderilemedi');
    } finally {
      setPendingLikes(prev => {
        const next = new Set(prev);
        next.delete(album.id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-xl text-amber-800">Yükleniyor...</div>
      </div>
    );
  }

  const allAlbums = [
    ...albums,
    ...(orphanCount > 0 ? [{
      id: 'genel',
      slug: 'genel',
      name: 'Genel',
      description: 'Albüm seçilmemiş fotoğraflar',
      cover_image: null,
      photo_count: orphanCount,
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-10 sm:py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14 fade-in">
          <div className="inline-flex items-center gap-2 mb-3 text-amber-600">
            <div className="w-8 h-px bg-amber-500" />
            <span className="text-xs font-semibold tracking-wider uppercase">Anılar</span>
            <div className="w-8 h-px bg-amber-500" />
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-amber-900 mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
            Foto Galeri
          </h1>
          <p className="text-base sm:text-lg text-amber-700 max-w-xl mx-auto">
            Albümlerimizden köyümüze dair anılar
          </p>
        </div>

        {allAlbums.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-amber-200 rounded-3xl p-10 sm:p-16 text-center">
            <ImageIcon className="w-12 h-12 mx-auto text-amber-300 mb-3" />
            <p className="text-amber-700 text-sm sm:text-base">Henüz albüm oluşturulmadı</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
            {allAlbums.map(album => (
              <Link
                key={album.id}
                to={`/galeri/${album.slug}`}
                className="group relative rounded-3xl overflow-hidden bg-white shadow-sm border border-amber-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`album-card-${album.slug}`}
              >
                <div className="relative h-56 sm:h-64 overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100">
                  {album.cover_image ? (
                    <img
                      src={album.cover_image}
                      alt={album.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-14 h-14 text-amber-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm text-xs font-semibold text-amber-800 shadow-sm">
                    {album.photo_count} fotoğraf
                  </div>
                  {album.id !== 'genel' && (
                    <button
                      type="button"
                      onClick={(e) => handleLike(e, album)}
                      disabled={pendingLikes.has(album.id) || likedAlbums.has(album.id)}
                      className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md text-xs font-semibold shadow-sm transition-all active:scale-95 ${
                        likedAlbums.has(album.id)
                          ? 'bg-rose-500 text-white cursor-default'
                          : 'bg-white/90 text-rose-600 hover:bg-white hover:scale-105'
                      }`}
                      aria-label="Albümü beğen"
                      data-testid={`album-like-${album.slug}`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${likedAlbums.has(album.id) ? 'fill-white' : ''}`} />
                      <span>{album.like_count || 0}</span>
                    </button>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-0.5" style={{ fontFamily: 'Merriweather, serif' }}>
                      {album.name}
                    </h3>
                    {album.description && (
                      <p className="text-amber-50/90 text-xs sm:text-sm line-clamp-1">{album.description}</p>
                    )}
                  </div>
                </div>
                <div className="px-5 py-3 flex items-center justify-between text-amber-700 text-sm font-medium">
                  <span>Albümü Aç</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
